<template>
  <div class="contract-history-section">
    <div v-if="venueGroups.length > 0">
      <!-- Venue Dropdown -->
      <div class="venue-selector mb-4">
        <el-select
          v-model="selectedVenueId"
          placeholder="Select a venue"
          style="width: 100%"
        >
          <el-option
            v-for="group in venueGroups"
            :key="group.venueId"
            :label="venueLabel(group)"
            :value="group.venueId"
          >
            <span>{{ group.venueName }}</span>
            <i v-if="group.hasActiveContract" class="fa fa-check ml-1 text-success"></i>
            <i v-if="group.isVoided" class="fa fa-ban ml-1 text-danger"></i>
          </el-option>
        </el-select>
      </div>

      <div v-if="selectedGroup" class="history-list">
        <div class="venue-history-group">
          <!-- Venue Header -->
          <div class="venue-header">
            <h4 class="venue-name">{{ selectedGroup.venueName }}</h4>
            <div class="venue-header-actions">
              <button
                v-if="selectedGroup.activeContract && ! isContractSignedByClient(selectedGroup)"
                class="btn btn-outline-primary btn-xs edit-contract-btn mr-1"
                :disabled="isContractActive(selectedGroup)"
                :title="isContractActive(selectedGroup) ? 'Contract has been sent to client and cannot be edited' : 'Edit Contract'"
                @click="$emit('edit-contract', selectedGroup.venueId, selectedGroup.activeContract)"
              >
                <i class="fa fa-edit"></i> Edit
              </button>
              <button
                v-if="canViewContract(selectedGroup)"
                class="btn btn-outline-info btn-xs mr-1"
                @click="openViewer(selectedGroup)"
                title="View Contract"
              >
                <i class="fa fa-eye"></i>
              </button>
              <button
                v-if="canDownloadContract(selectedGroup)"
                class="btn btn-outline-secondary btn-xs mr-1"
                :disabled="downloadingId === selectedGroup.venueId"
                @click="downloadContract(selectedGroup)"
                title="Download"
              >
                <i class="fa fa-download"></i>
              </button>
              <button
                v-if="canVoidContract(selectedGroup)"
                class="btn btn-outline-danger btn-xs mr-1"
                :disabled="voidingId === selectedGroup.venueId"
                @click="voidContract(selectedGroup)"
                title="Void Contract"
              >
                <i v-if="voidingId === selectedGroup.venueId" class="fa fa-spinner fa-spin"></i>
                <i v-else class="fa fa-ban"></i>
                {{ voidingId === selectedGroup.venueId ? 'Voiding...' : 'Void Contract' }}
              </button>
              <button
                v-if="canShowUploadButton(selectedGroup)"
                class="btn btn-outline-success btn-xs upload-contract-btn"
                :disabled="isContractActive(selectedGroup)"
                :title="isContractActive(selectedGroup) ? 'Contract has been sent to client and cannot be replaced' : 'Upload New'"
                @click="openUploadModal(selectedGroup.venueId)"
              >
                <i class="fa fa-upload"></i> Upload New
              </button>
            </div>
          </div>

          <!-- History Items -->
          <div class="history-items" v-if="selectedGroup.history.length > 0">
            <div
              v-for="(item, idx) in selectedGroup.history"
              :key="idx"
              class="history-item"
            >
              <div class="history-icon">
                <i :class="historyIcon(item.type)"></i>
              </div>
              <div class="history-event">
                {{ item.event }}
                <span
                  v-if="item.version > 1"
                  class="badge badge-secondary ml-1"
                  style="font-size:10px"
                >v{{ item.version }}</span>
                <el-tooltip
                  v-if="item.type === 'change_requested' && item.change_details"
                  :content="item.change_details"
                  placement="top"
                  effect="dark"
                  popper-class="change-details-tooltip"
                >
                  <i class="fa fa-info-circle text-info change-info-btn"></i>
                </el-tooltip>
              </div>
              <div class="history-timestamp">{{ formatTimestamp(item.timestamp) }}</div>
            </div>
          </div>

          <!-- No history yet for this venue -->
          <div v-else class="history-items">
            <div class="history-item">
              <div class="history-icon">
                <i class="fa fa-circle text-muted"></i>
              </div>
              <div class="history-event text-muted">No contract yet</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="no-history">
      <p class="text-muted">No contract history available.</p>
    </div>

    <!-- Upload Contract Modal -->
    <upload-contract-modal
      :visible="showUploadModal"
      @file-selected="onFileSelected"
      @close="showUploadModal = false"
    />

    <!-- Contract Viewer Modal -->
    <contract-viewer-modal
      :visible="showViewerModal"
      :contract="viewerContract"
      :lead-event-id="leadEventId"
      :venue-name="viewerVenueName"
      @close="closeViewer"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { Message, MessageBox, Select, Option } from 'element-ui';
import UploadContractModal from '@/components/Contracts/UploadContractModal.vue';
import ContractViewerModal from '@/components/Contracts/ContractViewerModal.vue';

export default {
  name: 'ContractHistoryDisplay',
  components: { UploadContractModal, ContractViewerModal, [Select.name]: Select, [Option.name]: Option },
  props: {
    venues: {
      type: Array,
      default: () => [],
    },
    leadEventId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      contractsData: {},
      downloadingId: null,
      voidingId: null,
      showUploadModal: false,
      pendingUploadVenueId: null,
      selectedVenueId: null,
      showViewerModal: false,
      viewerContract: null,
      viewerVenueName: '',
    };
  },
  computed: {
    venueGroups() {
      const list = (this.contractsData.data || []);
      return this.venues.map(venue => {
        const data = list.find(v => String(v.venue_id) === String(venue.id)) || {};
        // Prefer backend-marked active contract; otherwise pick the latest
        // non-voided entry from versions (handles cases where the active
        // contract field is null but a usable version still exists).
        let activeContract = data.active_contract || null;
        if (!activeContract && Array.isArray(data.versions) && data.versions.length) {
          const candidates = data.versions
            .filter(c => c && String(c.status || '').toLowerCase() !== 'voided')
            .slice()
            .sort((a, b) => (b.version || 0) - (a.version || 0));
          activeContract = candidates[0] || null;
        }
        const history = (data.history || []).slice().sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );
        const versions = Array.isArray(data.versions) ? data.versions : [];
        const hasActiveContract = !!activeContract;
        const anyVoided = versions.some(
          c => c && String(c.status || '').toLowerCase() === 'voided'
        ) || history.some(h => h.type === 'contract_voided');
        const isVoided = !hasActiveContract && anyVoided;
        const hasContractActivity = hasActiveContract
          || history.some(h => ['contract_sent', 'contract_signed', 'contract_opened'].includes(h.type));
        return {
          venueId: venue.id,
          venueName: venue.name,
          activeContract,
          history,
          hasActiveContract,
          isVoided,
          hasContractActivity,
        };
      });
    },
    selectedGroup() {
      if (!this.selectedVenueId) return null;
      return this.venueGroups.find(g => g.venueId === this.selectedVenueId) || null;
    },
    signedVenueId() {
      const signed = this.venueGroups.find(g => this.isContractSignedByClient(g));
      return signed ? signed.venueId : null;
    },
  },
  watch: {
    venueGroups(groups) {
      this.setDefaultVenue(groups);
    },
  },
  async created() {
    await this.reload();
  },
  methods: {
    venueLabel(group) {
      if (! group) return '';
      if (group.hasActiveContract) return group.venueName + ' ✓';
      if (group.isVoided) return group.venueName + ' ⊘';
      return group.venueName;
    },
    setDefaultVenue(groups) {
      if (!groups || !groups.length) return;
      // Preserve the user's current selection if it still exists in the
      // updated groups (e.g. after a reload following upload / send). Only
      // fall back to a default when nothing valid is selected.
      if (this.selectedVenueId
        && groups.some(g => g.venueId === this.selectedVenueId)) {
        return;
      }
      // Prefer a venue with an active contract, then one with any history, else first
      const withActive = groups.find(g => g.activeContract);
      const withActivity = groups.find(g => g.hasContractActivity);
      this.selectedVenueId = (withActive || withActivity || groups[0]).venueId;
    },
    async reload() {
      try {
        const data = await this.$store.dispatch('contracts/fetchContracts', {
          leadEventId: this.leadEventId,
        });
        this.contractsData = data || {};
        this.setDefaultVenue(this.venueGroups);
      } catch {
        // non-blocking — show empty state
      }
    },

    async downloadContract(group) {
      const attachments = group.activeContract?.attachments || [];
      const attachment = attachments.find(a => a.type === 'client_signed_copy')
        || attachments.find(a => a.type === 'vc_signed_copy')
        || attachments.filter(a => a.type === 'initial_contract').pop()
        || attachments[0];
      if (!attachment) return;

      this.downloadingId = group.venueId;
      try {
        const res = await this.$store.dispatch('contracts/getDownloadUrl', {
          leadEventId: this.leadEventId,
          attachmentId: attachment.id,
        });
        const url = (res && (res.download_url || res.url)) || res;
        window.open(url, '_blank', 'noopener');
      } catch {
        Message.error('Could not generate download link. Please try again.');
      } finally {
        this.downloadingId = null;
      }
    },

    historyIcon(type) {
      const map = {
        contract_created:  'fa fa-plus text-primary',
        contract_sent:     'fa fa-paper-plane text-warning',
        contract_opened:   'fa fa-eye text-info',
        contract_signed:   'fa fa-check text-success',
        contract_voided:   'fa fa-ban text-danger',
        change_requested:  'fa fa-edit text-warning',
      };
      return map[type] || 'fa fa-circle text-muted';
    },

    isContractActive(group) {
      const contract = group && group.activeContract;
      if (! contract) return false;

      const attachments = contract.attachments || [];
      const hasClientSignedCopy = attachments.some(a => a.type === 'client_signed_copy');
      const status = String(contract.status || '').toLowerCase();
      const isSignedStatus = status === 'signed';
      const isSentStatus = status === 'sent_to_client';

      return isSentStatus && ! hasClientSignedCopy && ! isSignedStatus;
    },

    isContractSignedByClient(group) {
      const contract = group && group.activeContract;
      if (! contract) return false;
      const attachments = contract.attachments || [];
      const hasClientSignedCopy = attachments.some(a => a.type === 'client_signed_copy');
      const status = String(contract.status || '').toLowerCase();
      return hasClientSignedCopy || status === 'signed';
    },

    canDownloadContract(group) {
      const contract = group && group.activeContract;
      if (! contract) return false;
      const attachments = contract.attachments || [];
      if (! attachments.length) return false;
      // Only allow download once the client has signed.
      return this.isContractSignedByClient(group);
    },

    canViewContract(group) {
      const contract = group && group.activeContract;
      if (! contract) return false;
      const attachments = contract.attachments || [];
      if (! attachments.length) return false;
      // Show the read-only preview at all times, except once the client has signed
      // (in that case the download button is shown instead).
      return ! this.isContractSignedByClient(group);
    },

    openViewer(group) {
      if (! group || ! group.activeContract) return;
      this.viewerContract = group.activeContract;
      this.viewerVenueName = group.venueName || '';
      this.showViewerModal = true;
    },

    closeViewer() {
      this.showViewerModal = false;
      this.viewerContract = null;
      this.viewerVenueName = '';
    },

    canVoidContract(group) {
      return this.isContractActive(group);
    },

    async voidContract(group) {
      if (!group || !group.activeContract || !this.canVoidContract(group)) return;

      try {
        await MessageBox.confirm(
          'This will void the current contract and stop the signing flow for this version. Continue?',
          'Void Contract',
          {
            confirmButtonText: 'Yes, Void Contract',
            cancelButtonText: 'Cancel',
            type: 'warning',
          }
        );
      } catch (_) {
        return;
      }

      this.voidingId = group.venueId;
      try {
        await this.$store.dispatch('contracts/deleteContract', {
          leadEventId: this.leadEventId,
          contractId: group.activeContract.id,
        });
        await this.reload();
        Message.success('Contract voided successfully.');
      } catch {
        Message.error('Failed to void contract. Please try again.');
      } finally {
        this.voidingId = null;
      }
    },

    canShowUploadButton(group) {
      if (! group) return false;
      // Hide when this venue's contract is already signed by the client.
      if (this.isContractSignedByClient(group)) return false;
      // Hide when another venue already has a client-signed contract –
      // only one signed contract is allowed per lead event.
      if (this.signedVenueId && this.signedVenueId !== group.venueId) return false;
      return true;
    },

    openUploadModal(venueId) {
      this.pendingUploadVenueId = venueId;
      this.showUploadModal = true;
    },

    onFileSelected(file) {
      this.showUploadModal = false;
      this.$emit('upload-contract', this.pendingUploadVenueId, file);
      this.pendingUploadVenueId = null;
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      const m = moment.utc(timestamp).local();
      const diff = moment().diff(m, 'days');
      const time = m.format('h:mm A');
      if (diff === 0) return `${time} - Today`;
      if (diff === 1) return `${time} - Yesterday`;
      return `${time} - ${m.format('D MMM YYYY')}`;
    },
  },
};
</script>

<style scoped>
.venue-history-group {
  margin-bottom: 30px;
}

.venue-header {
  margin-bottom: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.venue-name {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.venue-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.edit-contract-btn {
  opacity: 0.7;
  transition: all 0.3s ease;
  font-size: 10px;
  padding: 4px 8px;
}

.edit-contract-btn:hover:not(:disabled) {
  opacity: 1;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.edit-contract-btn:disabled,
.edit-contract-btn[disabled],
.upload-contract-btn:disabled,
.upload-contract-btn[disabled] {
  opacity: 0.45;
  cursor: not-allowed;
  background-color: #f5f5f5;
  border-color: #d9d9d9;
  color: #b0b0b0;
  box-shadow: none;
  transform: none;
  pointer-events: auto;
}

.btn-xs {
  font-size: 10px;
  padding: 4px 8px;
}

.history-items {
  padding-left: 0;
}

.history-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.history-item:last-child {
  border-bottom: none;
}

.history-icon {
  width: 25px;
  text-align: center;
  margin-right: 10px;
  flex-shrink: 0;
  font-size: 14px;
}

.history-event {
  flex: 1;
  font-weight: 400;
  color: #333;
}

.change-info-btn {
  margin-left: 6px;
  cursor: pointer;
  font-size: 13px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.change-info-btn:hover {
  opacity: 1;
}

.history-timestamp {
  font-size: 13px;
  color: #666;
  text-align: right;
  flex-shrink: 0;
}

.no-history p {
  text-align: center;
  padding: 16px 0;
}
</style>

<style>
.change-details-tooltip {
  max-width: 320px;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.4;
}
</style>
